import {
  BookOpenIcon,
  InformationCircleIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import useAxios from "axios-hooks";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import ClassroomForm from "./form/ClassroomForm";
import { CreateUserDto, UserResDto, UsersResDto } from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import moment from "moment";
import PaginationButtons from "../../components/pagination/PaginationButtons";
import { UserContext } from "../../App";

export function ClassroomsTable({ data }: { data: any[] | undefined }) {
  const navigate = useNavigate();
  const user = useContext(UserContext);

  return (
    <div className="overflow-x-auto rounded-xl table-container border border-base-300 bg-base-100">
      <table className="table table-md  w-full">
        <thead className="bg-base-200 sticky top-0 z-10 text-sm">
          <tr>
            <th>Name</th>
            <th>Students</th>
            <th>Instructors</th>
            <th>Creator</th>
            <th className="text-center">Created</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((classroom, index) => (
            <tr
              key={classroom._id}
              className="hover:bg-base-300 transition cursor-pointer"
              onClick={() => navigate(`/classrooms/${classroom._id}/students`)}
            >
              <td className="font-medium whitespace-nowrap">
                {classroom.name}
              </td>

              <td>
                <span className="badge badge-info badge-sm">
                  {classroom.users.filter(
                    (user: any) => user["group"] === "BUSINESS_MEMBER"
                  )?.length || 0}
                </span>
              </td>

              <td>
                <span className="badge badge-secondary badge-sm">
                  {classroom.users.filter(
                    (user: any) => user["group"] === "BUSINESS_INSTRUCTOR"
                  )?.length || 0}
                </span>
              </td>

              <td>
                {classroom.creator?.length ? (
                  <div className="text-sm leading-tight">
                    <div className="font-medium">
                      {classroom.creator[0].username}
                      {classroom.creator[0].userId === user?.sub && (
                        <span className="badge badge-sm badge-primary ml-1">
                          ME
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-base-content/70">
                      {classroom.creator[0].email}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-base-content/50 italic">
                    Unknown
                  </span>
                )}
              </td>

              <td className="text-center text-xs text-base-content/60">
                {moment(classroom.createdAt).fromNow()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Classrooms({
  classroomIds,
}: {
  classroomIds?: string[];
}) {
  const context = useOutletContext() as any;
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getClassrooms] = useAxios<UsersResDto>({
    url: "/classrooms",
    params: {
      search: searchString,
      classroomIds: classroomIds,
      page,
      limit: pageSize,
    },
  });

  useEffect(() => {
    context?.setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        <BookOpenIcon className="h-8 w-8" />
        Classrooms
      </div>
    );
    if (searchString !== undefined) {
      getClassrooms();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm"
            onClick={() => {
              (
                document?.getElementById("new_member_modal") as any
              )?.showModal();
            }}
          >
            <PlusIcon className="h-4"></PlusIcon>
            New Classroom
          </button>
        </div>
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <ClassroomsTable data={data?.docs}></ClassroomsTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="new_member_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-2">Create Classroom</h3>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <div>
            <ClassroomForm
              onSuccess={(user: UserResDto) => {
                navigate(`/classrooms/${user?._id}/students`);
              }}
            ></ClassroomForm>
          </div>
          {/* <div className="alert text-xs italic bg-transparent bg-base-100 border-0 opacity-50 p-0 mt-4">
            <InformationCircleIcon className="h-6"></InformationCircleIcon>
            You will be charged automatically for the new Member and an
            invitaion email with a temporary password will be sent to the member
            that you add.
          </div> */}
        </div>
      </dialog>
    </div>
  );
}
